import { useState, useEffect } from 'react'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import { useDeleteAdminPlan } from '@/hooks/useAdminPlans'
import type { ApiPlanSummary } from '@/types'

interface DeletePlanDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  plan: ApiPlanSummary | null
}

export function DeletePlanDialog({
  open,
  onOpenChange,
  plan,
}: DeletePlanDialogProps) {
  const [forceConfirmed, setForceConfirmed] = useState(false)
  const deleteMutation = useDeleteAdminPlan()

  // Reset checkbox when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setForceConfirmed(false)
    }
  }, [open])

  const isApproved = plan?.status === 'APPROVED'

  const handleDelete = () => {
    if (!plan) return
    deleteMutation.mutate(
      { planId: plan.id, force: isApproved },
      {
        onSuccess: () => {
          onOpenChange(false)
        },
      }
    )
  }

  if (!plan) return null

  // For APPROVED plans, show custom dialog with force-delete checkbox
  if (isApproved) {
    return (
      <Dialog
        open={open}
        onOpenChange={deleteMutation.isPending ? undefined : onOpenChange}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Delete Approved Plan</DialogTitle>
            <DialogDescription>
              You are about to delete &ldquo;{plan.name}&rdquo; which has been
              approved. This requires force-delete.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
              <p className="text-sm text-amber-700">
                This plan is approved. Deleting it may affect downstream
                processes. This action cannot be undone.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="force-delete"
                checked={forceConfirmed}
                onCheckedChange={checked => setForceConfirmed(checked === true)}
              />
              <label
                htmlFor="force-delete"
                className="cursor-pointer text-sm leading-none"
              >
                I understand, force-delete this approved plan
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={!forceConfirmed || deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Force Delete'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  // For non-APPROVED plans, use standard ConfirmationDialog
  return (
    <ConfirmationDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Delete Plan"
      description={`Are you sure you want to delete "${plan.name}"? This action cannot be undone.`}
      confirmText="Delete"
      cancelText="Cancel"
      onConfirm={handleDelete}
      variant="destructive"
      isLoading={deleteMutation.isPending}
    />
  )
}
